import React, { useEffect, useRef, useState } from 'react';
import './UI.css';

/**
 * ScrollReveal - Intersection Observer Animation Wrapper
 *
 * Reveals content as user scrolls, making pages feel lighter
 * and more engaging. Uses requestAnimationFrame for smooth performance.
 */
const ScrollReveal = ({
  children,
  delay = 0,
  threshold = 0.1,
  animation = 'fade-up', // 'fade-up', 'fade-in', 'slide-left', 'slide-right'
  once = true
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Use requestAnimationFrame for smooth animations
          requestAnimationFrame(() => {
            setTimeout(() => {
              setIsVisible(true);
            }, delay);
          });

          if (once) {
            observer.unobserve(entry.target);
          }
        } else if (!once) {
          setIsVisible(false);
        }
      },
      { threshold }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      if (elementRef.current) {
        observer.unobserve(elementRef.current);
      }
    };
  }, [delay, threshold, once]);

  return (
    <div
      ref={elementRef}
      className={`scroll-reveal reveal-${animation} ${isVisible ? 'is-visible' : ''}`}
    >
      {children}
    </div>
  );
};

/**
 * StaggerGroup - Stagger animation delays for children
 */
export const StaggerGroup = ({ children, staggerDelay = 100 }) => {
  return (
    <>
      {React.Children.map(children, (child, index) => (
        <ScrollReveal delay={index * staggerDelay}>
          {child}
        </ScrollReveal>
      ))}
    </>
  );
};

export default ScrollReveal;
